/**
 * Finds a country feature from GeoJSON data by country name
 * @param countryName - Name of the country to find
 * @param staticData - Optional static data object containing countries GeoJSON
 * @returns The country feature or null if not found
 */
export async function findCountryFeature(
  countryName: string,
  staticData?: Record<string, any>
): Promise<any | null> {
  try {
    let countriesData: any = null;

    if (staticData && staticData.countries) {
      countriesData = staticData.countries;
    } else {
      const response = await fetch("/data/map/layers/countries.geojson");
      if (!response.ok) {
        return null;
      }
      countriesData = await response.json();
    }

    if (!countriesData.features || !Array.isArray(countriesData.features)) {
      return null;
    }

    const normalizedSearchName = countryName.toLowerCase().trim();

    let countryFeature = countriesData.features.find((feature: any) => {
      const name = feature.properties?.name?.toLowerCase().trim();
      return name === normalizedSearchName;
    });

    if (!countryFeature) {
      countryFeature = countriesData.features.find((feature: any) => {
        const name = feature.properties?.name?.toLowerCase().trim();
        return (
          name.includes(normalizedSearchName) ||
          normalizedSearchName.includes(name)
        );
      });
    }

    return countryFeature || null;
  } catch (error) {
    return null;
  }
}

/**
 * Determines if a zoom level indicates a country-level location
 * @param zoom - Zoom level (undefined returns false)
 * @returns true if zoom is between 3 and 6 (inclusive)
 */
export function isCountryLocation(zoom: number | undefined): boolean {
  if (zoom === undefined) return false;
  return zoom >= 3 && zoom <= 6;
}

/**
 * Extracts unique category values from GeoJSON features
 * @param geoJSONData - GeoJSON data object with features array
 * @returns Sorted array of unique category values
 */
export function extractUniqueValuesFromGeoJSON(
  geoJSONData: any
): string[] {
  if (!geoJSONData?.features) return [];

  const uniqueValuesSet = new Set<string>();
  geoJSONData.features.forEach((feature: any) => {
    const category = feature.properties?._category;
    if (category != null && category !== "") {
      uniqueValuesSet.add(String(category).trim());
    }
  });
  return Array.from(uniqueValuesSet).sort();
}

